/**
 * Copyright (C) 2019 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { Accordion, AccordionDetails, AccordionSummary, Checkbox, FormControlLabel } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React from 'react';
import styled from 'styled-components';
import StyledProps from '../../common/theme/AppThemeInterface';
import Scrollbars from '../layout/Scrollbars';

export interface NetworkToggleProps {
  id: string;
  title: string;
  handleExpansionButtonClick: Function;
  handleCheckboxChange: (networkToggleItem: NetworkToggleItem, checked: boolean) => void;
  disabled: boolean;
  expanded: boolean;
  items: NetworkToggleItem[];
}

export interface NetworkToggleItem {
  id: string;
  label: string;
  checked: boolean;
  networkType: string;
  mapType: string;
}

const NetworkToggleCollapseGroupPanel = styled.div`
  margin: 8px 16px 0 24px;

  .expansion__panel {
    background-color: transparent;
    box-shadow: none;
  }

  .expansion__panel__summary {
    min-height: 0;
    padding: 0;
  }

  .expansion__panel__summary__content {
    margin: 0;
  }

  .expansion__panel__summary__expandIcon {
    right: 0;
    padding: 0;
  }

  .expansion__panel__details_root {
    margin: 0 7px 0 24px;
    padding: 0;
  }

  .expansion__panel__details_scroll {
    padding: 0 0 16px 0;
    box-sizing: border-box;
    max-height: none !important;

    div {
      display: flex;
      flex-direction: column;
      overflow-x: visible !important;
      padding: 10px 0;
    }
  }
`;

const LabeledCheckbox = styled(FormControlLabel)`
  height: 36px;

  .label {
    font-family: ${({ theme }: StyledProps) => theme.appFontFamily};
    font-size: 14px;
    color: white;
    line-height: 20px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
const NetworkToggle = (props: NetworkToggleProps) => {
  const { id, title, handleExpansionButtonClick, handleCheckboxChange, disabled, expanded, items } = props;

  const areAllSubItemsChecked = (): boolean => {
    return items && items.length > 0 && items.every((item: NetworkToggleItem) => item.checked);
  };

  const isAnySubItemChecked = (): boolean => {
    if (!items) return false;

    const checkedItemsOnly = items.filter((item: NetworkToggleItem) => item.checked);

    return checkedItemsOnly.length > 0 && checkedItemsOnly.length !== items.length;
  };

  const setCheckedForAllSubItems = (checked: boolean): void => {
    if (items) {
      items.forEach(item => {
        handleCheckboxChange(item, checked);
      });
    }
  };

  return (
    <NetworkToggleCollapseGroupPanel data-test={`network-toggle-${id}`}>
      <Accordion classes={{ root: 'expansion__panel' }} expanded={expanded && !disabled} disabled={disabled}>
        <AccordionSummary
          classes={{
            root: 'expansion__panel__summary',
            content: 'expansion__panel__summary__content',
            expandIconWrapper: 'expansion__panel__summary__expandIcon',
          }}
          expandIcon={
            <div>
              {/*
                  This outer div is necessary to avoid the following console error:
                  Warning: Failed prop type: Material-UI: you are providing an onClick event listener to a child of a button element.
                  Firefox will never trigger the event.
                  You should move the onClick listener to the parent button element.
                  https://github.com/mui-org/material-ui/issues/13957

                  Solution provided by: https://stackoverflow.com/questions/26402033/missing-click-event-for-span-inside-button-element-on-firefox
                 */}
              <ExpandMoreIcon
                data-test={`network-toggle-dropdown-${id}`}
                style={{ color: 'white', visibility: disabled ? 'hidden' : 'visible' }}
                onClick={() => {
                  handleExpansionButtonClick();
                }}
              />
            </div>
          }
        >
          <LabeledCheckbox
            classes={{ label: 'label' }}
            control={
              <Checkbox
                data-test={`network-overlays--${id}-network-checkbox`}
                checked={areAllSubItemsChecked()}
                indeterminate={isAnySubItemChecked()}
                style={{ color: 'white' }}
                disableRipple={true}
                value={id}
                onChange={(event, checked) => setCheckedForAllSubItems(checked)}
              />
            }
            label={title}
          />
        </AccordionSummary>

        <AccordionDetails classes={{ root: 'expansion__panel__details_root' }}>
          <Scrollbars className="expansion__panel__details_scroll" autoHeight autoHeightMin={0}>
            {items
              ? items.map(item => (
                  <LabeledCheckbox
                    key={item.id}
                    classes={{ label: 'label' }}
                    control={
                      <Checkbox
                        data-test={`network-overlays-${item.label}-beam-checkbox`}
                        checked={item.checked}
                        style={{ color: 'white' }}
                        disableRipple={true}
                        value={item.id}
                        onChange={(event, checked) => {
                          handleCheckboxChange(item, checked);
                        }}
                      />
                    }
                    label={item.label}
                  />
                ))
              : null}
          </Scrollbars>
        </AccordionDetails>
      </Accordion>
    </NetworkToggleCollapseGroupPanel>
  );
};
export default NetworkToggle;
